import { LoadingButton } from "@mui/lab";
import {
    Box,
    Grid,
    LinearProgress,
    Theme,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useRouteMatch } from "react-router";
import { nomineeOptOutApplicationPreview, nomineeOptOutGenerateOtp } from "../../redux-store/actions/application";
import { ApplicationProps, NomineeOptOutApplicationPreviewResponseBody } from "../../redux-store/types/api-types";
import { showError } from "../../redux-store/actions/auth";
import dayjs from "../../utils/dayjs";


type ItemDetailsProps<T> = {
    label: string;
    value: T;
};

function ItemDetails<T>({
    label,
    value
}: ItemDetailsProps<T>): JSX.Element {
    return (
        <Grid
            item
            xs={12}
            md={3}
            sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "10px",
                alignItems: { xs: "flex-start", md: "center" },
                textAlign: { xs: "left", md: "center" },
            }
            }
        >
            <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                    color: theme.palette.text.secondary
                })}
            >
                {label}
            </Typography>

            <Typography
                variant="body_600"
                sx={(theme: Theme) => ({
                    color: theme.palette.text.primary,
                })}
            >
                {value}
            </Typography>
        </ Grid>
    );
}


export default function NomineeOptOutDetails(): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();
    const { params } = useRouteMatch();
    const { nomineeOptOutId } = params as unknown as { nomineeOptOutId: string };


    const [isLoading, setLoading] = useState<boolean>(false)
    const [applicationPreviewData, setApplicationPreviewData] = useState<NomineeOptOutApplicationPreviewResponseBody | null>(null)
    const [isGenerateOtpLoading, setGenerateOtpLoading] = useState<boolean>(false)

    const fetchApplicationPreviewData = async () => {
        try {
            setLoading(true)

            const response = await dispatch(nomineeOptOutApplicationPreview(nomineeOptOutId)) as unknown as NomineeOptOutApplicationPreviewResponseBody;

            setApplicationPreviewData(response)

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchApplicationPreviewData()
    }, [])

    const investorData = [
        {
            label: "Application Id",
            value: applicationPreviewData?.id ?? "-"
        },
        {
            label: "Investor Name",
            value: applicationPreviewData?.name ?? "-"
        },
        {
            label: "PAN",
            value: applicationPreviewData?.panNumber ?? "-"
        },
        {
            label: "Date of Birth",
            value: applicationPreviewData?.dob ? dayjs(applicationPreviewData.dob).format("DD-MM-YYYY") : "-"
        }
    ]

    const handleGenerateOtp = async () => {
        try {
            setGenerateOtpLoading(true)

            const response = await dispatch(nomineeOptOutGenerateOtp({
                applicationId: Number(applicationPreviewData?.id ?? ""),
                token: nomineeOptOutId ?? ""
            })) as unknown as ApplicationProps;

            if (!response) {
                return
            }

            if (!response?.isSuccess) {
                dispatch(showError(response?.message ?? ""))
                return
            }

            if (response?.isSuccess) {
                history.push('validate-otp', {
                    message: response?.message ?? "",
                    otpRecordId: Number(response?.otpRecordId ?? ""),
                    token: response?.token ?? "",
                    applicationId: Number(response?.applicationId ?? "")
                })
                return
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setGenerateOtpLoading(false)
        }
    }

    return (
        <Box sx={{ padding: '30px 45px' }}>
            <Box sx={(theme: Theme) => ({ borderBottom: `0.5px solid ${theme.palette.customColors.pastelGrey}` })}>
                <Typography
                    variant="h1"
                    sx={(theme: Theme) => ({
                        lineHeight: "32.5px",
                        color: theme.palette.text.primary,
                        mb: 1,
                        textAlign: "center"
                    })}
                >
                    Nominee Opt Out Authentication
                </Typography>
            </Box>
            {isLoading && <LinearProgress />}
            {!isLoading && (
                <Box
                    sx={(theme: Theme) => ({
                        borderRadius: "20px",
                        backgroundColor: theme.palette.common.white,
                        padding: "25px 30px 35px 20px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        marginTop: "25px",
                        width: { xs: "100%", md: "80%" },
                        marginX: "auto"
                    })}
                >
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            justifyContent: "space-evenly",
                            marginTop: 2
                        }}
                    >
                        {investorData.map((obj) => (
                            <ItemDetails key={obj.label} label={obj.label} value={obj.value} />
                        ))}
                    </Grid>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <LoadingButton
                            loading={isGenerateOtpLoading}
                            loadingPosition="center"
                            variant="contained"
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_600,
                                marginTop: "50px",
                                padding: "10px 15px",
                                borderRadius: "10px",
                                backgroundColor: theme.palette.primary[500],
                                color: theme.palette.textDark.primary,
                            })}
                            onClick={handleGenerateOtp}
                        >
                            Generate OTP
                        </LoadingButton>
                    </Box>
                </Box>
            )
            }
        </Box >
    )
}
